import React, { useEffect, useRef, useState } from "react";

// Define the props type
interface LazyLoadImageProps {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
}

const LazyLoadImage: React.FC<LazyLoadImageProps> = ({
  src,
  alt = "",
  style,
  className,
}) => {
  // State to track if the image is in the viewport
  const [isIntersecting, setIsIntersecting] = useState(false);

  // Ref to attach to the image element
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Return early if no src is provided
    if (!src) return;

    // Create an Intersection Observer to detect when the image enters the viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Set state to load the image when it is in the viewport
            setIsIntersecting(true);
            // Disconnect the observer once the image is loaded
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the image is in the viewport
      }
    );

    // Observe the image element if the ref is attached
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [src]); // Re-run the effect if src changes

  // Create a props object for the image element
  const imgProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    ref: imgRef,
    src: isIntersecting ? src : "",
    alt,
    loading: "lazy",
  };

  // Conditionally add style and className if they are provided
  if (style) {
    imgProps.style = style;
  }
  if (className) {
    imgProps.className = className;
  }
  return <img {...imgProps} />;
};

export default LazyLoadImage;
